import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NewsCard from '../components/NewsCard';
import CategoryFilter from '../components/CategoryFilter';
import RegionFilter from '../components/RegionFilter';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Category() {
  const { categoryId } = useParams();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);

  const isWorldCategory = categoryId === 'world';

  const fetchCategoryNews = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/news?category=${categoryId}&limit=50`;
      if (selectedRegion) {
        url += `&region=${selectedRegion}`;
      }
      const response = await axios.get(url);
      setNews(response.data);

      const categoriesRes = await axios.get(`${API}/categories`);
      const cat = categoriesRes.data.find(c => c.id === categoryId);
      setCategoryName(cat ? cat.name : categoryId);
    } catch (error) {
      console.error('Failed to fetch category news:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryId, selectedRegion]);

  useEffect(() => {
    fetchCategoryNews();
  }, [fetchCategoryNews]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CategoryFilter activeCategory={categoryId} />

        {/* Region Filter only shows for World category */}
        {isWorldCategory && (
          <WorldRegionFilter
            activeRegion={selectedRegion}
            onRegionChange={setSelectedRegion}
          />
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          key={`${categoryId}-${selectedRegion}`}
        >
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-8">
            {categoryName}
          </h1>

          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="loading-indicator">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055FF]"></div>
            </div>
          ) : news.length === 0 ? (
            <p className="text-center text-[#52525B] dark:text-[#A1A1AA] py-16">Keine Nachrichten in dieser Kategorie</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-news-grid">
              {news.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                >
                  <NewsCard news={item} />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

// Inline region filter that uses state instead of navigation
function WorldRegionFilter({ activeRegion, onRegionChange }) {
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/regions`).then(res => setRegions(res.data)).catch(console.error);
  }, []);

  return (
    <div className="mb-8" data-testid="world-region-filter">
      <div className="flex items-center mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] dark:text-[#A1A1AA]">
          Land auswählen
        </h3>
      </div>
      <div className="flex flex-wrap gap-2 pb-2">
        <button
          onClick={() => onRegionChange(null)}
          className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
            !activeRegion
              ? 'bg-[#0055FF] text-white'
              : 'bg-white dark:bg-[#121212] text-gray-700 dark:text-gray-300 border border-black/10 dark:border-white/10 hover:border-[#0055FF] dark:hover:border-[#3377FF]'
          }`}
          data-testid="world-region-all"
        >
          Alle Länder
        </button>
        {regions.map((region) => (
          <button
            key={region.id}
            onClick={() => onRegionChange(region.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeRegion === region.id
                ? 'bg-[#0055FF] text-white'
                : 'bg-white dark:bg-[#121212] text-gray-700 dark:text-gray-300 border border-black/10 dark:border-white/10 hover:border-[#0055FF] dark:hover:border-[#3377FF]'
            }`}
            data-testid={`world-region-${region.id}`}
          >
            {region.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Category;
